/*
 * ConfirmModal — reusable, promise-based confirmation dialog.
 * Usage:
 *   const confirmed = await confirm({
 *     title: "Broadcast to entire network?",
 *     body: "This will SMS every eligible driver…",
 *     confirmLabel: "Broadcast",
 *     tone: "danger",  // "danger" | "primary"
 *   });
 *   if (!confirmed) return;
 *
 * Render <ConfirmHost /> once at the app root (already added in App.js).
 */
import React, { useCallback, useEffect, useState } from "react";

let externalTrigger = null;

export function confirm(opts) {
  if (!externalTrigger) {
    // Fallback to window.confirm if host not mounted yet.
    return Promise.resolve(window.confirm(opts?.body || opts?.title || "Are you sure?"));
  }
  return externalTrigger(opts);
}

export default function ConfirmHost() {
  const [state, setState] = useState(null); // { opts, resolve }

  useEffect(() => {
    externalTrigger = (opts) =>
      new Promise((resolve) => setState({ opts: opts || {}, resolve }));
    return () => { externalTrigger = null; };
  }, []);

  const close = useCallback((val) => {
    if (state?.resolve) state.resolve(val);
    setState(null);
  }, [state]);

  useEffect(() => {
    if (!state) return;
    const onKey = (e) => {
      if (e.key === "Escape") close(false);
      if (e.key === "Enter") close(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, close]);

  if (!state) return null;
  const {
    title = "Are you sure?",
    body = "",
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    tone = "primary",
  } = state.opts;

  const btnClass =
    tone === "danger"
      ? "bg-red-600 hover:bg-red-700 text-white"
      : "bg-forest hover:brightness-95 text-white";

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[10001] grid place-items-center p-4"
      onClick={() => close(false)}
      data-testid="confirm-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-lg font-bold text-slate-900 mb-2">{title}</div>
        {body && <div className="text-sm text-slate-600 mb-5 whitespace-pre-line">{body}</div>}
        <div className="flex justify-end gap-2">
          <button
            onClick={() => close(false)}
            className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm text-slate-700"
            data-testid="confirm-cancel"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => close(true)}
            className={`px-4 py-2 rounded-md text-sm font-semibold ${btnClass}`}
            data-testid="confirm-ok"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
